// 参考POIラベルのカテゴリ定義 (コンビニ・公園など「位置の参考になる」施設)。
// setup-map / 避難マップ / 喫煙所マップの3サブシステムが共有する。
//
// Mapbox: streets-v12 の poi_label は feature に maki(アイコン種別) と
//   filterrank(重要度: 1=主要 … 5=些末) を持つ。maki でカテゴリ抽出、filterrank で足切り。
//   class=building の建物名ラベルは参考にならないため maki 指定で自然に除外される。

/** 参考POIラベルのカテゴリ定義 (POI_CATEGORIES の要素)。 */
export interface PoiCategory {
    key: string;
    label: string;
    /** streets-v12 poi_label の maki (アイコン種別) 値。 */
    maki: string[];
    /**
     * maki では分けられないものを class で絞る。
     *
     * 著名企業のビル (class=landmark: 三菱ビル・大手町タワー等) と、目印にならない
     * 汎用ビル (class=building: はとバス喫煙所・ギフトショップ等) は **maki が
     * どちらも 'marker'** で、maki だけでは分離できない (実測: 丸の内〜博多の
     * 12地点で landmark 74件 / building 277件)。
     */
    classes?: string[];
    /** class より細かい type 指定 (class=commercial_services の Company 等)。 */
    types?: string[];
    /** 店舗名/支店名がつくチェーン系 (「店舗名を隠す」トグルの対象)。 */
    brand?: boolean;
}

// brand: 店舗名/支店名がつくチェーン系 (コンビニ等)。「店舗名を隠す」トグルの対象。
// 公園・学校・病院・公共施設は名前自体が目印なので brand=false (名前を残す)。
export const POI_CATEGORIES: PoiCategory[] = [
    { key: 'convenience', label: 'コンビニ',        maki: ['convenience'],                                   brand: true },
    { key: 'supermarket', label: 'スーパー・商店',  maki: ['grocery', 'clothing-store'],                     brand: true },
    // 商業ビル・複合施設 (丸ビル/KITTE 等)。streets-v12 では maki='shop'(class=store_like,
    // type=Mall) で来る。位置の目印になるので既定オン。ビル名は残す (brand=false)。
    { key: 'building',    label: '建物',            maki: ['shop', 'shopping'],                              brand: false },
    { key: 'food',        label: '飲食店',          maki: ['restaurant', 'cafe', 'fast-food', 'bar', 'ice-cream'], brand: true },
    { key: 'park',        label: '公園',            maki: ['park', 'garden'] },
    { key: 'school',      label: '学校',            maki: ['school', 'college'] },
    { key: 'hospital',    label: '病院',            maki: ['hospital', 'doctor', 'dentist'] },
    { key: 'pharmacy',    label: '薬局',            maki: ['pharmacy'] },
    { key: 'bank',        label: '銀行・ATM',       maki: ['bank'],                                          brand: true },
    { key: 'public',      label: '公共施設',        maki: ['town-hall', 'post', 'police', 'fire-station', 'library'] },
    { key: 'fuel',        label: 'ガソリン・駐車',  maki: ['fuel', 'parking', 'charging-station'],           brand: true },
    // 著名企業のビル。maki は汎用ビルと同じ 'marker' なので class/type で絞る。
    // 経路をたどる目印として質が高い (三菱ビル・大手町タワー・日鉄日本橋ビル等)。
    { key: 'landmark',    label: '著名企業・ビル',  maki: [], classes: ['landmark'], types: ['Company'] },
    // ホテルは大きく名前が知られていて目印になる (東京ステーションホテル等)。
    { key: 'hotel',       label: 'ホテル',          maki: ['lodging'] },
];

/** 参考POIの既定表示カテゴリ (最も位置参照に使うもの)。建物(商業ビル)を既定オン。 */
export const DEFAULT_POI_CATEGORIES: string[] = ['convenience', 'building', 'park'];

/** 選択カテゴリ keys → Mapbox の class 値リスト (重複除去)。maki で分けられない分。 */
export function classesForCategories(keys: string[] | null | undefined): string[] {
    const set = new Set<string>();
    for (const cat of POI_CATEGORIES) {
        if (keys && keys.includes(cat.key)) (cat.classes ?? []).forEach((c) => set.add(c));
    }
    return Array.from(set);
}

/** 選択カテゴリ keys → Mapbox の type 値リスト (重複除去)。 */
export function typesForCategories(keys: string[] | null | undefined): string[] {
    const set = new Set<string>();
    for (const cat of POI_CATEGORIES) {
        if (keys && keys.includes(cat.key)) (cat.types ?? []).forEach((t) => set.add(t));
    }
    return Array.from(set);
}

/** 選択カテゴリ keys → Mapbox の maki 値リスト (重複除去)。 */
export function makiForCategories(keys: string[] | null | undefined): string[] {
    const set = new Set<string>();
    for (const cat of POI_CATEGORIES) {
        if (keys && keys.includes(cat.key)) cat.maki.forEach((m) => set.add(m));
    }
    return Array.from(set);
}

/** 店舗名を隠す対象 (brand=true カテゴリ) の maki 値リスト。 */
export function brandMaki(): string[] {
    const set = new Set<string>();
    for (const cat of POI_CATEGORIES) {
        if (cat.brand) cat.maki.forEach((m) => set.add(m));
    }
    return Array.from(set);
}

/**
 * 旧カテゴリキー 'medical' (病院・薬局) を 'hospital'+'pharmacy' の2キーへ分割した
 * ことに伴う後方互換移行。既存 localStorage の referenceLabels.categories をロード時に
 * 正規化する (maki 集合は不変なので表示挙動は保たれる)。'medical' が無ければ現状返し。
 */
export function migrateLegacyPoiCategories(categories: string[]): string[] {
    const hasMedical = categories.includes('medical');
    // 旧 'supermarket' (grocery+shop+clothing+shopping) を supermarket(grocery+
    // clothing) + building(shop+shopping) に分割したため、既存設定は building を
    // 補って従来の表示集合 (商業ビル名を含む) を保つ。
    const needsBuilding =
        categories.includes('supermarket') && !categories.includes('building');
    if (!hasMedical && !needsBuilding) return categories;
    const out: string[] = [];
    for (const c of categories) {
        if (c === 'medical') {
            if (!out.includes('hospital')) out.push('hospital');
            if (!out.includes('pharmacy')) out.push('pharmacy');
        } else if (c === 'supermarket') {
            if (!out.includes('supermarket')) out.push('supermarket');
            if (!out.includes('building')) out.push('building');
        } else if (!out.includes(c)) {
            out.push(c);
        }
    }
    return out;
}
